import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { authService } from '../../../services/authService';
import { userManagementService } from '../../../services/userManagementService';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Debug: Show available test accounts
  const showTestAccounts = () => {
    alert(`Debug Info:
Available Test Accounts:
- Email: d.morawietz@vierdimensional.com
- Name: dejan
- Status: Check your Supabase dashboard

Or use mock credentials:
- Email: admin@vibecoding.de  
- Password: VibeCoding2024!`);
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData?.email) {
      newErrors.email = 'E-Mail-Adresse ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // First try Supabase authentication
      try {
        const result = await authService?.signIn(formData?.email, formData?.password);
        
        if (result?.user && result?.session) {
          // Get user profile
          const profile = await userManagementService?.getCurrentUser();
          
          // Check if user is active
          if (profile?.status !== 'active') {
            setErrors({
              general: 'Ihr Konto ist nicht aktiv. Bitte wenden Sie sich an den Administrator.'
            });
            return;
          }

          // Store authentication state
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userEmail', formData?.email);
          localStorage.setItem('userId', result?.user?.id);
          localStorage.setItem('userRole', profile?.role || 'user');
          
          if (formData?.rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          }

          console.log('Login successful:', formData?.email);
          
          // Navigate to dashboard
          navigate('/ai-agents-dashboard');
          return;
        }
      } catch (supabaseError) {
        console.error('Supabase login failed:', supabaseError?.message);
        
        // If Supabase fails, fall back to mock authentication
        const mockCredentials = {
          email: 'admin@vibecoding.de',
          password: 'VibeCoding2024!'
        };

        if (formData?.email === mockCredentials?.email && formData?.password === mockCredentials?.password) {
          // Store authentication state for mock user
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userEmail', formData?.email);
          localStorage.setItem('userRole', 'admin');
          
          if (formData?.rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          }

          console.log('Mock login successful:', formData?.email);
          navigate('/ai-agents-dashboard');
          return;
        }

        // Show specific error message based on error type
        if (supabaseError?.message?.includes('Invalid login credentials') || 
            supabaseError?.message?.includes('Email not confirmed')) {
          setErrors({
            general: `Login fehlgeschlagen: ${supabaseError?.message}

Mögliche Lösungen:
1. Überprüfen Sie E-Mail und Passwort
2. E-Mail-Bestätigung erforderlich (prüfen Sie Ihren Posteingang)
3. Verwenden Sie die Passwort-Reset-Funktion
4. Kontaktieren Sie den Administrator`
          });
        } else if (supabaseError?.message?.includes('Cannot connect') || 
                   supabaseError?.message?.includes('project may be paused')) {
          setErrors({
            general: 'Verbindung zum Authentifizierungsservice fehlgeschlagen. Das Supabase-Projekt könnte pausiert sein. Verwenden Sie die Mock-Anmeldung (admin@vibecoding.de) oder kontaktieren Sie den Administrator.'
          });
        } else {
          setErrors({
            general: `Anmeldung fehlgeschlagen: ${supabaseError?.message}`
          });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        general: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData?.email) {
      setErrors({
        email: 'Bitte geben Sie Ihre E-Mail-Adresse ein, um das Passwort zurückzusetzen.'
      });
      return;
    }

    try {
      setIsLoading(true);
      await authService?.resetPassword(formData?.email);
      alert('Passwort-Reset-E-Mail wurde gesendet. Bitte überprüfen Sie Ihren Posteingang.');
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Fehler beim Senden der Passwort-Reset-E-Mail. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Debug Info Button */}
        <div className="text-center">
          <button
            type="button"
            onClick={showTestAccounts}
            className="text-xs text-blue-500 hover:text-blue-400 underline"
          >
            🔧 Debug: Verfügbare Testkonten anzeigen
          </button>
        </div>

        {/* General Error */}
        {errors?.general && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-1" />
              <div className="text-sm text-error whitespace-pre-line">{errors?.general}</div>
            </div>
          </div>
        )}

        {/* Email Field */}
        <Input
          label="E-Mail-Adresse"
          type="email"
          name="email"
          placeholder="d.morawietz@vierdimensional.com"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        {/* Password Field */}
        <div className="relative">
          <Input
            label="Passwort"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Geben Sie Ihr Passwort ein"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
            disabled={isLoading}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>

        {/* Remember Me */}
        <div className="flex items-center justify-between">
          <Checkbox
            label="Angemeldet bleiben"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-colors focus-ring rounded"
            disabled={isLoading}
          >
            Passwort vergessen?
          </button>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          iconName="LogIn"
          iconPosition="left"
        >
          {isLoading ? 'Anmeldung läuft...' : 'Anmelden'}
        </Button>

        {/* Quick Test Login */}
        <div className="text-center pt-2">
          <button
            type="button"
            onClick={() => {
              setFormData({
                email: 'admin@vibecoding.de',
                password: 'VibeCoding2024!',
                rememberMe: false
              });
            }}
            className="text-xs text-gray-500 hover:text-gray-400 underline"
            disabled={isLoading}
          >
            🚀 Mock-Anmeldung verwenden
          </button>
        </div>

        {/* Register Link */}
        <div className="text-center pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Noch kein Konto?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-primary hover:text-primary/80 font-medium transition-colors focus-ring rounded"
              disabled={isLoading}
            >
              Jetzt registrieren
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;